import {
  Breadcrumb as WfpBreadcrumb,
  BreadcrumbHome,
  BreadcrumbItem,
} from '@wfp/react';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import { REPORT_DETAIL_DATA_KEY } from 'src/redux/constants';
import { RootState } from 'src/redux/store';
import { Route, RoutesConfig } from 'src/types';

function getBreadcrumbs(
  routesConfig: RoutesConfig,
  pathname: string,
  country?: string,
) {
  const breadcrumbs: { name: string; path: string }[] = [
    {
      name: routesConfig.root.name || 'Home',
      path: routesConfig.root.path,
    },
  ];
  const pathSegments = pathname.split('/').filter(Boolean);

  let currentPath = '/';
  for (const segment of pathSegments) {
    currentPath = `${currentPath}${segment}/`;

    const matchedRoute = findRouteByPath(
      routesConfig.root.sub_routes,
      currentPath,
    );
    if (matchedRoute) {
      const params = extractParams(matchedRoute.path, currentPath);

      const name = matchedRoute.matchToName
        ? matchedRoute.matchToName({ params }, country)
        : matchedRoute.name;

      breadcrumbs.push({ name, path: currentPath });
    }
  }

  if (!breadcrumbs.length) {
    const matchedRoute = findRouteByPath(
      routesConfig.root.sub_routes,
      pathname,
    );
    if (matchedRoute) {
      breadcrumbs.push({
        name: matchedRoute.name,
        path: pathname,
      });
    }
  }

  return breadcrumbs;
}

function normalizePath(path: string) {
  return path.replace(/\/+$/, '').replace(/\/{2,}/g, '/');
}

function findRouteByPath(
  routes: Record<string, Route>,
  path: string,
): Route | null {
  for (const key in routes) {
    const route = routes[key];
    const normalizedPath = normalizePath(route.path);

    if (matchPath({ path: normalizedPath, end: true }, path)) {
      return route;
    }

    if (route.sub_routes) {
      const found = findRouteByPath(route.sub_routes, path);
      if (found) return found;
    }
  }
  return null;
}

function extractParams(
  routePath: string,
  currentPath: string,
): Record<string, string> {
  const normalizedRoutePath = normalizePath(routePath);
  const match = matchPath(
    { path: normalizedRoutePath, end: true },
    currentPath,
  );
  return match?.params || {};
}

const Breadcrumb: FC = () => {
  const location = useLocation();
  const routesConfig = useSelector((state: RootState) => state.routesConfig);
  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY]?.data,
  );

  if (reportDetailData?.permissions?.has_permission_to_view_report === false) {
    return null;
  }

  const breadcrumbs = getBreadcrumbs(
    routesConfig as unknown as RoutesConfig,
    location.pathname,
    reportDetailData?.country,
  );

  return (
    <WfpBreadcrumb className="main-side-padding">
      {breadcrumbs.map((crumb, index) => {
        return (
          <BreadcrumbItem key={crumb.path}>
            <NavLink
              to={crumb.path}
              data-test-id={index === 0 ? 'home-link' : 'year-link'}
            >
              {index === 0 ? (
                <BreadcrumbHome
                  hometext={crumb.name}
                  data-test-id="home-icon"
                />
              ) : (
                crumb.name
              )}
            </NavLink>
          </BreadcrumbItem>
        );
      })}
    </WfpBreadcrumb>
  );
};

export default Breadcrumb;
